/**
 * Static copy + layout variant for Onboarding2 annexure “fill” previews (no API).
 * Keys match `Onboarding2AnnexureGridRow.id` in `onboarding2Model.ts`.
 */

import { ANNEXURE_15B_SELF_CERTIFICATION_TEMPLATE } from '../../constants/vmsAnnexureTemplates';
import {
  MONTHLY_ANNEXURE_11_ROW_IDS,
  MONTHLY_PERFORMANCE_INDICATORS,
} from './ongoingVendorReviewViewsModel';

export type Ob2AnnexureFormVariant =
  | 'comments-two-col'
  | 'comments-rating-two-col'
  | 'monthly-performance-two-col'
  | 'questionnaire-sections'
  | 'kyc-two-col'
  | 'owner-response-two-col';

export type Ob2AnnexureQuestion = {
  id: string;
  text: string;
  /** DB row id for registered-vendor fill */
  storageRowId?: string;
  /** Last row spans both columns on md+ (Annexure 11 overall outcome). */
  fullWidth?: boolean;
};

export type Ob2AnnexureQuestionnaireQuestion = {
  /** Annexure definition row id (e.g. `a1`, `n15b_28`) for binding `{rowId}_{column}` in registered-vendor fill. */
  storageRowId?: string;
  /** Optional legacy global index; when omitted, the UI numbers 1…n within each section (Code 4 reference). */
  num?: number;
  /** Badge number override (e.g. Process section repeats 7 and 8 on the last row). */
  badgeNum?: number;
  text: string;
  /** When true, shows a small “Required” hint on the card. */
  required?: boolean;
  /** Code 6 KYC: maroon “Upload files” control beside the answer field. */
  inlineUpload?: boolean;
  /** Code 15A: Yes/No control instead of a free-text response field. */
  answerKind?: 'text' | 'yesno';
  /** Code 15B: display code before question text (e.g. `1.1`). */
  questionCode?: string;
  /** Code 15B: per-row “Comments” textarea under the question (default true when `questionCode` is set). */
  /** Code 15A Information Security: optional Comment under each Yes/No row (default on when omitted). */
  inlineComment?: boolean;
  /** Code 15B (Security Assessment): line under the question (“Mandatory/Discretionary: …”). */
  obligatory?: 'Mandatory' | 'Discretionary';
  /** Code 15A IT/IS: span full width in a two-column question grid (`questionGridTwoColumn` on section). */
  questionFullWidth?: boolean;
  /** Code 15A IT/IS: bold subsection title row rendered full width above this question (starts a new block). */
  subsectionTitleBefore?: string;
  /** Code 15A IT/IS: show Upload beside Response (overrides section `code15aItIsShowUpload` when set). */
  showRowUpload?: boolean;
};

export type Ob2AnnexureQuestionnaireSubsection = {
  id: string;
  title: string;
  questions: Ob2AnnexureQuestionnaireQuestion[];
};

export type Ob2AnnexureQuestionnaireSection = {
  id: string;
  title: string;
  /** Code 15B: parallel `storageRowIds[i]` for `questions[i]` when set. */
  storageRowIds?: string[];
  /** Code 15A: internal assessment textarea binds to this definition row’s `response` column. */
  internalAssessmentStorageRowId?: string;
  /** Code 4 / 15B: synthetic upload slot id (allowlisted) for section-level supporting documents. */
  sectionUploadSlotId?: string;
  questions: Ob2AnnexureQuestionnaireQuestion[];
  /** Shown when `questionnaireUi` is default (completion line). */
  completionLabel: string;
  /** Sectional comments box at bottom. Default true for default UI; hidden for `onboarding2Code4` unless true. */
  showSectionalComments?: boolean;
  /** Code 4-style footer: e.g. `0.00/100` in maroon after “Section weighting score:”. */
  weightingScoreDisplay?: string;
  /** Renders Supporting Document upload inside this section (below the question grid). */
  inlineSupportingDocument?: boolean;
  /** Layout for the inline Supporting Document strip. */
  inlineSupportingDocumentVariant?: 'stacked' | 'inlineLink' | 'stackedCentered';
  /** Maroon (`maroon`) or red (`red`) accents inside Supporting Document strip. */
  supportingDocumentUploadTone?: 'maroon' | 'red';
  /** Override annexure-level badge shape for this section (e.g. Process uses circles). */
  sectionNumberBadge?: 'square' | 'circle';
  /** Summary title shown in all caps (e.g. COST). */
  sectionTitleAllCaps?: boolean;
  /** Grey label style for “Supporting Document” heading above the upload strip. */
  supportingDocumentTitleMuted?: boolean;
  /** Custom hint under upload (e.g. hyphen instead of middle dot). */
  supportingDocumentSizeHint?: string;
  /** Link label inside dashed upload region (default “Upload Files”; e.g. “Browse Files”). */
  supportingDocumentUploadLinkText?: string;
  /** Visible `<h3>` for inline supporting upload (default “Supporting Document”). */
  supportingDocumentStripHeading?: string;
  /** When there is exactly one question, span full width on md+ grid. */
  fullWidthSingleQuestionRow?: boolean;
  /** Overrides annexure-level `fieldLabel` for inputs in this section (e.g. Code 6 “Business owner”). */
  sectionFieldLabel?: string;
  /** Code 15A: grouped sub-headings (e.g. 1.1) each with their own Yes/No rows. */
  subsections?: Ob2AnnexureQuestionnaireSubsection[];
  /** Label for the sectional comments textarea (Code 15A). */
  sectionCommentsLabel?: string;
  /** Code 15A IT/IS: lead line above the question grid (e.g. “1.1 The Vendor…”). */
  subsectionLead?: string;
  /** Code 15A IT/IS: two-column grid on md+ for questions (use `questionFullWidth` on items that span both columns). */
  questionGridTwoColumn?: boolean;
  /** Code 15A IT/IS: force a single-column question list (no `md:grid-cols-2`). */
  questionGridSingleColumn?: boolean;
  /** Code 15A IT/IS: per-section hide “Upload Files” beside Response (default: show upload). */
  code15aItIsShowUpload?: boolean;
  /** Code 15A IT/IS: internal-only textarea at end of section. */
  internalAssessmentBlock?: { label: string; placeholder?: string };
  /** Code 15B: render Supporting Document above the question list (e.g. Data Security). */
  supportingDocumentBeforeQuestions?: boolean;
  /** Code 15B: optional second slot after questions when both before/after uploads are needed. */
  sectionUploadSlotIdEnd?: string;
};

export type Ob2AnnexureFormDefinition = {
  annexureId: string;
  variant: Ob2AnnexureFormVariant;
  /** `onboarding2Code4`: VMS questionnaire ref — square badges, per-section numbering, weighting score footer. */
  /** `onboarding2Code15a`: Yes/No + optional per-row comment (legacy / alternate 15A layout). */
  /** `onboarding2Code15aItIs`: Code 15A IT & IS questionnaire — coded badges, Response + Upload per row. */
  /** `onboarding2Code15b`: Security assessment — self-certification strip, accordion domains, pink cards, Data Request Input, one Supporting Document. */
  questionnaireUi?:
    | 'default'
    | 'onboarding2Code4'
    | 'onboarding2Code6'
    | 'onboarding2Code15a'
    | 'onboarding2Code15aItIs'
    | 'onboarding2Code15b';
  /** Shown under the title (e.g. question count). */
  subtitle: string;
  /** Optional bold heading above questionnaire sections (e.g. Code 15A “Risk assessment”). */
  questionnaireLeadHeading?: string;
  /** Optional bold heading rendered directly above the two-column question grid (e.g. Code 9 “Risk Aspect”). */
  questionGridHeading?: string;
  /** Extra blocks after the main grid (risk table, upload, approvers…) */
  showRatingSummaryTable?: boolean;
  showSupportingUpload?: boolean;
  showApproversStakeholders?: boolean;
  /** Flat question list (two-col grid) */
  questions?: Ob2AnnexureQuestion[];
  /** Collapsible sections + two-col grid */
  sections?: Ob2AnnexureQuestionnaireSection[];
  /** Label above the main text field */
  fieldLabel: string;
  /** Square vs circle index badge */
  numberBadge: 'square' | 'circle';
  /** Code 15B (Security Assessment): self-certification banner with optional template download. */
  selfCertificationBanner?: {
    title: string;
    downloadLabel?: string;
    downloadHref?: string;
    downloadFileName?: string;
  };
  /** Code 15B: signed self-cert upload block after questionnaire sections (before approvers). */
  selfCertificationUploadBlock?: { title?: string; description: string };
  /** Optional full-width remarks (unused for Code 15B security layout). */
  remarksBlock?: { heading?: string; placeholder?: string };
  /** Optional amber note above footer (unused for Code 15B security layout). */
  questionnaireFooterNote?: string;
};

const Q = (id: string, text: string, storageRowId?: string, fullWidth?: boolean): Ob2AnnexureQuestion => ({
  id,
  text,
  ...(storageRowId ? { storageRowId } : {}),
  ...(fullWidth ? { fullWidth } : {}),
});

const A11_MONTHLY_QUESTIONS: Ob2AnnexureQuestion[] = MONTHLY_PERFORMANCE_INDICATORS.map((text, i) => {
  const n = i + 1;
  return Q(`a11-${n}`, text, MONTHLY_ANNEXURE_11_ROW_IDS[i], n === MONTHLY_PERFORMANCE_INDICATORS.length);
});

/** Code 15A IT/IS: question row with coded badge, Response field, and Upload Files (UI forces upload row). */
const it15 = (
  questionCode: string,
  text: string,
  questionFullWidth = false,
  subsectionTitleBefore?: string,
  storageRowId?: string,
  showRowUpload?: boolean,
): Ob2AnnexureQuestionnaireQuestion => ({
  questionCode,
  text,
  required: false,
  questionFullWidth,
  ...(storageRowId ? { storageRowId } : {}),
  ...(subsectionTitleBefore ? { subsectionTitleBefore } : {}),
  ...(showRowUpload !== undefined ? { showRowUpload } : {}),
});

const DEFINITIONS: Record<string, Ob2AnnexureFormDefinition> = {
  a4: {
    annexureId: 'a4',
    variant: 'questionnaire-sections',
    questionnaireUi: 'onboarding2Code4',
    subtitle: 'Complete every section before submitting for approval.',
    fieldLabel: 'Response',
    numberBadge: 'square',
    sections: [
      {
        id: 'business-profile',
        title: 'Business Profile',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        inlineSupportingDocument: true,
        sectionUploadSlotId: 'n4_sec_business_profile',
        questions: [
          { text: 'What Is The Registered Name Of Your Company?', required: false, storageRowId: 'a1' },
          {
            text: "What Is Your Company's Registration Number? Is It Registered In The UK?",
            required: false,
            storageRowId: 'a2',
          },
          { text: 'How Many Directors Are At Present? Please Provide Their Names.', required: false, storageRowId: 'a3' },
          { text: 'What Is The Shareholding Pattern Of The Company?', required: false, storageRowId: 'a4' },
          { text: 'The Financial Statement Of The Company', required: false, storageRowId: 'a5' },
          { text: 'How Long Has The Company Been In Business?', required: false, storageRowId: 'a6' },
          { text: "What Are The Company's Areas Of Expertise?", required: false, storageRowId: 'a7' },
          { text: 'Define The Scope Of Work You Will Be Carrying Out For Us.', required: false, storageRowId: 'a8' },
        ],
      },
      {
        id: 'customer-base-references',
        title: 'Customer Base and References',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'inlineLink',
        sectionUploadSlotId: 'n4_sec_customer_refs',
        questions: [
          {
            text: 'Please Provide The Names And The Contact Details Of Your Last Three Outsourcing Contracts For Reference.',
            required: false,
            storageRowId: 'b1',
          },
          {
            text: 'Please Provide The Detail Of Work Carried Out For The Above Clients.',
            required: false,
            storageRowId: 'b2',
          },
          {
            text: 'How Long Have You Provided The Proposed Service?',
            required: false,
            storageRowId: 'b3',
          },
        ],
      },
      {
        id: 'people-and-management',
        title: 'People and Management',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        sectionUploadSlotId: 'n4_sec_people_mgmt',
        questions: [
          {
            text: 'Describe The Composition Of Your Dedicated Team. What Is The Role Of Each Member?',
            required: false,
            storageRowId: 'c1',
          },
          {
            text: 'Who Will Be Our Contact Person/Relationship Manager? Please Provide The Contact Details.',
            required: false,
            storageRowId: 'c2',
          },
          {
            text: 'What Is The Turnaround Time For Resolution Of Issues Faced, If Any?',
            required: false,
            storageRowId: 'c3',
          },
          { text: 'What Are The Escalation Procedures?', required: false, storageRowId: 'c4' },
          {
            text: 'How Does Your Company Keep Up To Date With The Latest Industry Knowledge And Standards?',
            required: false,
            storageRowId: 'c5',
          },
          { text: 'Do You Use Subcontractors For Any Work Performed For The Client?', required: false, storageRowId: 'c6' },
          { text: 'Do You Employ Minors For Any Of Your Work?', required: false, storageRowId: 'c7' },
          {
            text: 'Do You And Any Sub-Contractor Comply With Modern Slavery Act?',
            required: false,
            storageRowId: 'c8',
          },
        ],
      },
      {
        id: 'process',
        title: 'Process',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentUploadTone: 'red',
        sectionUploadSlotId: 'n4_sec_process',
        questions: [
          {
            badgeNum: 1,
            text: 'What Relevant Security Certification For The Scope Of Work Has The Company Achieved?',
            required: false,
            storageRowId: 'd1',
          },
          {
            badgeNum: 2,
            text: 'Do You Have A Defined Procedures Manual? How Do You Ensure The Same Is Being Followed?',
            required: false,
            storageRowId: 'd2',
          },
          {
            badgeNum: 3,
            text: 'Are Your Processes Compliant With UK GDPR Regulations?',
            required: false,
            storageRowId: 'd3',
          },
          {
            badgeNum: 4,
            text: 'What All Activities Is Included In Your Service Level?',
            required: false,
            storageRowId: 'd4',
          },
          {
            badgeNum: 5,
            text: 'What Are Your Reporting Guidelines For Delivery And Performance?',
            required: false,
            storageRowId: 'd5',
          },
          {
            badgeNum: 6,
            text: 'When And How Will We Receive Our Invoices?',
            required: false,
            storageRowId: 'd6',
          },
          {
            badgeNum: 7,
            text: 'What Will Be The Payment Timelines?',
            required: false,
            storageRowId: 'd7',
          },
          {
            badgeNum: 8,
            text: 'Would You Allow Us To Get A Third-Party Audit Undertaken At Your Site For Our Contract Related Activities?',
            required: false,
            storageRowId: 'd8',
          },
          {
            badgeNum: 7,
            text: 'Any Adverse Comments On The Last Audit Report Of Your Firm? If Yes, Please Provide Details Of The Same.',
            required: false,
            storageRowId: 'd9',
          },
          {
            badgeNum: 8,
            text: 'What Is The On-Boarding Process?',
            required: false,
            storageRowId: 'd10',
          },
        ],
      },
      {
        id: 'security',
        title: 'Security',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        sectionUploadSlotId: 'n4_sec_security',
        questions: [
          {
            text: 'Is There Any Data Breach Incident Reported By Your Firm In The Past 3 Years? If Yes, What Was The Resolution And Action Taken.',
            required: false,
            storageRowId: 'e10',
          },
          {
            text: 'Is There Any Fine / Penalty Imposed By The Regulator Due To Non-Compliance Of Regulatory Guidelines / Breaches In The Past 3 Years? If Yes, Please Provide Details Of The Same.',
            required: false,
            storageRowId: 'e11',
          },
          {
            text: 'Please Comment On Log Management And Its Monitoring And Incident Response System Of Your Firm.',
            required: false,
            storageRowId: 'e13',
          },
          {
            text: 'Please Comment On Fraud Management Controls In Place For Your Firm.',
            required: false,
            storageRowId: 'e14',
          },
        ],
      },
      {
        id: 'cost',
        title: 'COST',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        sectionTitleAllCaps: true,
        fullWidthSingleQuestionRow: true,
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentUploadTone: 'maroon',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n4_sec_cost',
        questions: [
          {
            text: 'What Is Your Pricing Model: Flat Fee? Or Tiered Pricing With Volume Discounts?',
            required: false,
            storageRowId: 'g1',
          },
        ],
      },
      {
        id: 'reporting',
        title: 'Reporting',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        fullWidthSingleQuestionRow: true,
        questions: [
          {
            num: 1,
            text: 'What Reports Are Available',
            required: false,
            storageRowId: 'h1',
          },
        ],
      },
      {
        id: 'supporting-document-a',
        title: 'Supporting Document',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        questions: [],
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentUploadTone: 'maroon',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file · max 10.0 MB',
        sectionUploadSlotId: 'n4_sec_support_a',
      },
      {
        id: 'supporting-document-b',
        title: 'Supporting Document',
        completionLabel: '—',
        weightingScoreDisplay: '0.00/100',
        showSectionalComments: false,
        questions: [],
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentUploadTone: 'maroon',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: '',
        sectionUploadSlotId: 'n4_sec_support_b',
      },
    ],
  },
  a5: {
    annexureId: 'a5',
    variant: 'comments-two-col',
    subtitle: 'Questions 1–22 · 22 questions',
    fieldLabel: 'Comments',
    numberBadge: 'square',
    questions: [
      Q('a5-1', 'Past Experience Of Conducting The Outsourced Activity', 'n5_1'),
      Q('a5-2', 'Financial Soundness', 'n5_2'),
      Q('a5-3', 'Competence To Implement And Support Proposed Activities Over The Contractual Period', 'n5_3'),
      Q('a5-4', 'Ability To Service Commitments Even In Adverse Conditions', 'n5_4'),
      Q('a5-5', 'Business Reputation And Culture', 'n5_5'),
      Q('a5-6', 'AML Compliance Arrangements', 'n5_6'),
      Q('a5-7', 'Complaints Process', 'n5_7'),
      Q('a5-8', 'Outstanding Or Potential Litigations', 'n5_8'),
      Q('a5-9', 'Security And Internal Control', 'n5_9'),
      Q('a5-10', 'Audit Coverage Reporting And Monitoring Environment', 'n5_10'),
      Q('a5-11', 'Business Continuity Management', 'n5_11'),
      Q(
        'a5-12',
        'Any External Factors Affecting The Company Or Activity Like Political, Economic And Social',
        'n5_12',
      ),
      Q(
        'a5-13',
        'Legal Environment Of Jurisdiction In Which The Service Provider Operates And Other Events That May Impact Service Performance',
        'n5_13',
      ),
      Q('a5-14', 'Business Continuity Arrangements In Case Of Technology Outsourcing', 'n5_14'),
      Q('a5-15', 'Due Diligence For Sub-Service Providers', 'n5_15'),
      Q('a5-16', 'Materiality', 'n5_16'),
      Q('a5-17', 'Risk Management Framework', 'n5_17'),
      Q('a5-18', 'Secure Infrastructure Facilities', 'n5_18'),
      Q('a5-19', 'Employee Training Knowledge Transfer', 'n5_19'),
      Q('a5-20', 'Reliance On An Ability To Deal With Sub-Contractors', 'n5_20'),
      Q('a5-21', 'Date Presented To Committee For Approval', 'n5_21'),
      Q('a5-22', 'Outcome', 'n5_22'),
    ],
  },
  a6: {
    annexureId: 'a6',
    variant: 'questionnaire-sections',
    questionnaireUi: 'onboarding2Code6',
    subtitle: 'Complete all sections before submitting for approval.',
    fieldLabel: 'Answer',
    numberBadge: 'square',
    sections: [
      {
        id: 'general-information',
        title: 'General Information',
        completionLabel: '—',
        showSectionalComments: false,
        sectionFieldLabel: 'Department RM',
        questions: [
          { text: 'Business Registration', required: false, inlineUpload: true, storageRowId: 'n6_1' },
          { text: 'Business License/Regulations (If Applicable)', required: false, storageRowId: 'n6_2' },
          { text: 'Comprehensive List Of All Services', required: false, inlineUpload: true, storageRowId: 'n6_3' },
          { text: 'Locations', required: false, storageRowId: 'n6_4' },
          { text: 'Information On Executives', required: false, storageRowId: 'n6_5' },
          { text: 'Ownership Structure', required: false, inlineUpload: true, storageRowId: 'n6_6' },
          { text: 'Principal Owners/UBOs', required: false, storageRowId: 'n6_7' },
          { text: 'List Of Clients', required: false, inlineUpload: true, storageRowId: 'n6_8' },
        ],
      },
      {
        id: 'financial-capacity',
        title: 'Financial & Capacity Review',
        completionLabel: '—',
        showSectionalComments: false,
        questions: [
          { text: 'Financial Statements/Audit Report', required: false, inlineUpload: true, storageRowId: 'n6_9' },
          { text: 'Business Continuity Plan', required: false, storageRowId: 'n6_10' },
          { text: 'Disaster Recovery Plan', required: false, storageRowId: 'n6_11' },
          { text: 'Capacity Review', required: false, storageRowId: 'n6_12' },
        ],
      },
      {
        id: 'risk-assessment',
        title: 'Risk Assessment',
        completionLabel: '—',
        showSectionalComments: false,
        questions: [
          {
            text: 'Negative News Report (Complaints & Negative Reviews)',
            required: false,
            inlineUpload: true,
            storageRowId: 'n6_13',
          },
          { text: 'World Check & Sanction Screening', required: false, inlineUpload: true, storageRowId: 'n6_14' },
          { text: 'Operational Risk', required: false, storageRowId: 'n6_15' },
          { text: 'Legal Risk', required: false, storageRowId: 'n6_16' },
          { text: 'Anti Slavery Policy', required: false, storageRowId: 'n6_17' },
          { text: 'Cyber Security Risk', required: false, inlineUpload: true, storageRowId: 'n6_18' },
          { text: 'Data Protection', required: false, storageRowId: 'n6_19', questionFullWidth: true },
        ],
      },
      {
        id: 'insurance',
        title: 'Insurance',
        completionLabel: '—',
        showSectionalComments: false,
        questions: [
          { text: 'General Liability', required: false, storageRowId: 'n6_20' },
          { text: 'Insurance Specific To Services', required: false, storageRowId: 'n6_21' },
        ],
      },
    ],
  },
  a11: {
    annexureId: 'a11',
    variant: 'monthly-performance-two-col',
    subtitle: 'Seventeen performance indicators · observations and remarks per item',
    questionGridHeading: 'Performance Indicators',
    fieldLabel: 'Observations',
    numberBadge: 'square',
    showSupportingUpload: true,
    questions: A11_MONTHLY_QUESTIONS,
  },
  a12: {
    annexureId: 'a12',
    variant: 'comments-rating-two-col',
    subtitle: 'Six checklist points · observations and low / medium / high risk per item',
    questionGridHeading: 'Checklist Point',
    fieldLabel: 'Observations',
    numberBadge: 'square',
    showSupportingUpload: true,
    questions: [
      Q('a12-1', 'Physical And Cyber Security Control', 'n12_1'),
      Q('a12-2', 'Evaluation Of Health And Safety Policy And Procedures', 'n12_2'),
      Q('a12-3', 'ISO Certification Visible In The Building', 'n12_3'),
      Q('a12-4', 'Interaction With Employee For Operational Activity', 'n12_4'),
      Q('a12-5', 'Over All Working Atmosphere In The Vendor Location', 'n12_5'),
      Q('a12-6', 'Overall Comments', 'n12_6'),
    ],
  },
  a9: {
    annexureId: 'a9',
    variant: 'comments-rating-two-col',
    subtitle: 'Nine risk criteria · comments and low / medium / high rating per row',
    questionGridHeading: 'Risk Aspect',
    fieldLabel: 'Comments',
    numberBadge: 'square',
    showRatingSummaryTable: true,
    showSupportingUpload: true,
    showApproversStakeholders: true,
    questions: [
      Q('a9-1', 'Employment Pool', 'n9_1'),
      Q('a9-2', 'Infrastructure', 'n9_2'),
      Q('a9-3', 'Cost Saving – Directly And Indirectly', 'n9_3'),
      Q('a9-4', 'Quality Of Services', 'n9_4'),
      Q('a9-5', 'Time Difference', 'n9_5'),
      Q('a9-6', 'Language Factor', 'n9_6'),
      Q('a9-7', 'Process Competitiveness', 'n9_7'),
      Q('a9-8', 'Regulation', 'n9_8'),
      Q('a9-9', 'Sanction Country Involvement', 'n9_9', true),
    ],
  },
  a13: {
    annexureId: 'a13',
    variant: 'owner-response-two-col',
    subtitle: 'Nine criteria · Department RM response per question',
    fieldLabel: 'Department RM Response',
    numberBadge: 'square',
    showSupportingUpload: true,
    showApproversStakeholders: true,
    questions: [
      Q(
        'a13-1',
        'Business Continuity Standard For The Full Range Of Products, Services And Works You Provide?',
        'n13_1',
      ),
      Q(
        'a13-2',
        'Have You Assessed The Risks To Your Operations And Taken The Necessary Actions To Protect?',
        'n13_2',
      ),
      Q(
        'a13-3',
        'Do You Identify And Mitigate Risks Or Threats To The Business Operations From Specific Events Such As Warnings Of Heavy Weather, System Failure, And Power Outages?',
        'n13_3',
      ),
      Q(
        'a13-4',
        'Do You Have A Business Continuity Strategy For People, Premises, Technology, Information, Suppliers And Stakeholders?',
        'n13_4',
      ),
      Q(
        'a13-5',
        'Have You Regularly Updated Business Continuity Arrangement That Includes Your Incident Management Process, Notification Procedures, Recovery Strategy/Procedures And The Estimated Recovery Time?',
        'n13_5',
      ),
      Q(
        'a13-6',
        'Can You Provide Information On Your BCP Exercising Program And Evidence Of Your Most Recent Exercises?',
        'n13_6',
      ),
      Q(
        'a13-7',
        'Are All Staff Aware Of The BC Procedures And Their Roles And Responsibilities Within Them?',
        'n13_7',
      ),
      Q('a13-8', 'Do You Have A Proven And Effective IT Continuity Plan? Please Provide Evidence', 'n13_8'),
      Q('a13-9', 'Is All Critical Data Backed Up And Readily Available Offsite?', 'n13_9'),
    ],
  },
  a15a: {
    annexureId: 'a15a',
    variant: 'questionnaire-sections',
    questionnaireUi: 'onboarding2Code15aItIs',
    subtitle:
      'IT and IS questionnaire: policies, compliance, controls, BCP/contingency, product and service design, with evidence uploads and internal assessment where shown.',
    fieldLabel: 'Response',
    numberBadge: 'square',
    sections: [
      {
        id: 'policies-standards-procedures',
        title: 'Policies, Standards and Procedures',
        completionLabel: '—',
        showSectionalComments: false,
        questionGridTwoColumn: true,
        subsectionLead: '1.1 The Vendor Has Formal Documented & Approved:',
        code15aItIsShowUpload: false,
        internalAssessmentStorageRowId: 'n15a_16',
        internalAssessmentBlock: {
          label: "Internal Assessment On Policy, Procedures Controls: (For PNBIL's Internal Use)",
          placeholder: 'Write here...',
        },
        questions: [
          it15('1.1.1', 'Information Security Policies & Procedures.'),
          it15('1.1.2', 'Incident Response Procedures Including Data Breach Handling.'),
          it15('1.1.3', 'Change Management Procedures.'),
          it15('1.1.4', 'BCP Policy & BCP Procedures.'),
          it15(
            '1.1.5',
            'Third Party/Outsourcing Policy & Procedures. (Including Contractors, Subcontractors, Vendors, Outsourcing Ventures, Or Other External Third-Party Contracts).',
          ),
          it15(
            '1.1.6',
            'Policy To Protect Client Information Against Unauthorised Access; Whether Stored, Printed, Spoken Or Transmitted.',
          ),
          it15('1.1.7', 'Client Support With Escalation Procedures.'),
          it15(
            '1.1.8',
            'Termination Or Job Transfer Procedures That Immediately Protect Unauthorised Access To Information.',
          ),
          it15('1.1.9', 'Policy For Regular User Information Security Awareness Programs', true),
          it15(
            '1.2',
            'Performs Background Checks For Individuals Handling Confidential Information.',
            true,
          ),
          it15(
            '1.3',
            'Formal Routine Information Security Risk Management Program For Risk Assessments And Risk Management',
            true,
          ),
          it15(
            '1.4',
            "Cyber Insurance Cover. Does It Cover The Vendor's Client Losses?",
            true,
          ),
          it15(
            '1.5',
            'Has A Designated CISO(Chief Information Security Officer) And The Level Of Management IS Is Owned.',
            true,
          ),
          it15(
            '1.6',
            'Willing To Provide Copies Of Above Mention Policies/Procedures /Standards (If Mentioned Yes)',
            true,
          ),
        ],
      },
      {
        id: 'compliance-the-vendor',
        title: 'Compliance: The vendor:',
        completionLabel: '—',
        showSectionalComments: false,
        questionGridTwoColumn: true,
        code15aItIsShowUpload: false,
        internalAssessmentStorageRowId: 'n15a_22',
        internalAssessmentBlock: {
          label: "Internal Assessment On Compliance Controls: (For PNBIL's Internal Use)",
          placeholder: 'Write here...',
        },
        questions: [
          it15(
            '2.1',
            'Security Program Development Standards: Is Certified On ISO Compliance Standard Eg : ISO 27001, 9001, 22301 Etc As Applicable To Their Service Industry.',
            false,
            undefined,
            'n15a_17',
            true,
          ),
          it15(
            '2.2',
            'PCI-DSS Compliance: To Provide Documentation Of Its PCI-DSS Compliance If The Vendor Manages Any Payment Card Information. (E.G. AoC, RoC, SAQs Etc As Required).',
            false,
            undefined,
            'n15a_18',
            true,
          ),
          it15(
            '2.3',
            'Process Management: Process Management Development Standards/Model E.G: ITIL, Six Sigma Or CMMI Etc.',
            false,
            undefined,
            'n15a_19',
            true,
          ),
          it15(
            '2.4',
            'Security Controls Standards: Security Controls Development Standards Like COBIT5 , NIST SP 800 Or COSO.',
            false,
            undefined,
            'n15a_20',
            false,
          ),
          it15(
            '2.5',
            'Application Security: Uses Industry Standard Best Practices For Application Security (Eg OWASP).',
            false,
            undefined,
            'n15a_21',
            true,
          ),
        ],
      },
      {
        id: 'controls',
        title: 'Controls',
        completionLabel: '—',
        showSectionalComments: false,
        questionGridTwoColumn: true,
        code15aItIsShowUpload: false,
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15a_sec_controls',
        internalAssessmentStorageRowId: 'n15a_40',
        internalAssessmentBlock: {
          label: "Internal Assessment On IS Controls: (For PNBIL's Internal Use)",
          placeholder: 'Write here...',
        },
        questions: [
          it15(
            '3.1',
            'Provide your overall response for access controls (physical & logical).',
            true,
            '3.1 Access Controls (Physical & Logical)',
          ),
          it15(
            '3.1.1',
            'Controls Access To Secure Areas. E.G. Key Distribution Management (Both Physical And Electronic), Paper/Electronic Logs, Monitoring Of Facility Doors, Etc.',
          ),
          it15(
            '3.1.2',
            'Controls Access To Server Rooms And Follows Least Privilege And Need-To-Know Practices For Those Facilities.',
          ),
          it15(
            '3.1.3',
            'Immediately Removes, Or Modifies Access, When Personnel Terminate, Transfer, Or Change Job Functions.',
          ),
          it15(
            '3.1.4',
            'Achieves Individual Accountability By Assigning Unique IDs And Prohibiting Password Sharing.',
          ),
          it15('3.1.5', 'User/ Access Reviews Frequency.'),
          it15(
            '3.2',
            'Provide your overall response for network security, firewall and IS-related controls.',
            true,
            '3.2 Network Security/ Firewall & IS Related Controls:',
          ),
          it15('3.2.1', 'Implements Network Firewall Protection.'),
          it15('3.2.2', 'Has IDS/IPS Technology Implemented.'),
          it15('3.2.3', 'Uses Enterprise Virus Protection On All Systems.'),
          it15(
            '3.2.4',
            'Remote Access Is Granted? How The Accountability & Secured Access Is Granted?',
          ),
          it15(
            '3.2.5',
            'IS Controls Audits/Cyber Audits Are Being Conducted? What Is The Frequency?',
          ),
          it15(
            '3.3',
            'Provide your overall response for software development and deployment controls.',
            true,
            '3.3 Software Development & Deployment Controls:',
          ),
          it15(
            '3.3.1',
            'Uses Separate Physical And Logical Development, Test And Production Environments And Databases, And Use Artificial Data In Both Development & Test Environments.',
          ),
          it15('3.3.2', 'Change Deployment Controls Or Tools?'),
          it15('3.3.3', 'Record Of Knowledge Of Base Building For The Software Changes?'),
        ],
      },
      {
        id: 'contingency-bcp-plan-assessment',
        title: 'Contingency & BCP plan assessment',
        completionLabel: '—',
        showSectionalComments: false,
        questionGridTwoColumn: true,
        code15aItIsShowUpload: false,
        internalAssessmentStorageRowId: 'n15a_44',
        internalAssessmentBlock: {
          label: 'Internal Assessment On BCP/Contingency Controls (For PNBMetLife Internal Use)',
          placeholder: 'Write here...',
        },
        questions: [
          it15(
            '4.1',
            'Is Emergency Procedures And Responsibilities Documented And Stored Securely At Multiple Sites.',
            true,
          ),
          it15(
            '4.2',
            'Is There Any BCP Site At Remote Server Location? How The Business Continuity Is Ensured In Case Of Any Disruption?',
            true,
          ),
          it15(
            '4.3',
            'Are There Any Backup Tapes? What Is The Frequency Of Offsite Backup Storage And How Frequent By These Are Being Tested?',
            true,
          ),
        ],
      },
      {
        id: 'product-service-design',
        title: 'Product/Service Design',
        completionLabel: '—',
        showSectionalComments: false,
        questionGridTwoColumn: true,
        code15aItIsShowUpload: false,
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15a_sec_product_svc',
        internalAssessmentStorageRowId: 'n15a_71',
        internalAssessmentBlock: {
          label: 'Internal Assessment On Compliance Controls (For PNBMetLife Internal Use)',
          placeholder: 'Write here...',
        },
        questions: [
          it15(
            '5.1',
            'What Is The Model Of The Proposed Application/Process? (E.g. Cloud Hosting, On-Premise, Stand-Alone Etc.)',
          ),
          it15(
            '5.2',
            'What Technologies (Programming Languages, DB Tools, Framework Set Etc) Are Being Used To Develop This Product?',
          ),
          it15('5.3', 'Is Annual Technical Support Part Of The Service/Product Offered?'),
          it15(
            '5.4',
            'Are The Release Notes, Hardware Document Or User Manuals Part Of The Vendor Delivery?',
          ),
          it15(
            '5.5',
            'Vendor Agrees For The Escrow Arrangement And Who Are The Preferred Escrow Managers?',
          ),
          it15(
            '5.6',
            'Are Your Test Systems Hosted In The Same Manner As Your Production Systems?',
          ),
          it15(
            '5.7',
            'Are Security Professionals Involved In The Testing Phase Of An Application?',
          ),
          it15(
            '5.8',
            'Does The Service Or Product Support Include Fixing Of Penetration Testing & Audits Observations?',
          ),
          it15(
            '5.9',
            'Provide your response for cloud hosting arrangements where applicable.',
            true,
            '5.9 In Case Of Cloud Hosting',
          ),
          it15('5.9.1', 'Where These Servers Are Hosted?'),
          it15('5.9.2', 'What Is The Compliance Level Of That Data Centre?'),
          it15('5.9.3', 'What Is The Uptime Availability Of The Data Centre?'),
          it15(
            '5.9.4',
            "Whether A Visit Of Bank's Officials To The Data Centre Can Be Arranged?",
          ),
          it15(
            '5.9.5',
            'How The Secured Access Is Ensured? Is There Any Multi Factor Or Multilevel Authentication Proposed?',
          ),
          it15(
            '5.9.6',
            'How Server Hardware Performance & Capacity Utilization Is Monitored?',
          ),
          it15(
            '5.9.7',
            'Is There Any Local Copy Of Data / Information Available Or Can Be Downloaded? What Is The Procedure For Data Handover?',
          ),
          it15('5.9.8', 'Is There Any Offsite Tape Backup Stored Anywhere?'),
          it15(
            '5.10',
            'Provide your response for on-premise or stand-alone arrangements where applicable.',
            true,
            '5.10 In Case Of On-Premise/Stand-Alone Model',
          ),
          it15(
            '5.10.1',
            'Share The Details Of The Hardware, Software & System Specifications Required For The Product/Service Implementation?',
          ),
          it15(
            '5.10.2',
            "Is There Any Specific Technical Expertise Required At Bank's End For Maintaining The Product?",
          ),
          it15(
            '5.10.3',
            'What Is The Database Size & What Is The Incremental Size? Can The DB Be Purged & Archived? Does It Come With Extra Cost?',
          ),
          it15(
            '5.10.4',
            'Is There A Remote Access Required For Supporting The On-Premise Product/ Software? Is The Access To Be Privileged?',
          ),
          it15(
            '5.10.5',
            "Is The On-Premise Product Integrable With The Bank's Active Directory Authentication For Easy SSO(Single Sign On)?",
            true,
          ),
          it15(
            '5.11',
            'Provide your response for services requiring system access where applicable.',
            true,
            '5.11 In Case Of Services Requiring System Access',
          ),
          it15(
            '5.11.1',
            'Access To What Kind Of Data Is Required For The Said Service? Public, But Confidential, Extra Confidential, Data Regulatory Data',
          ),
          it15(
            '5.11.2',
            'What Level Of Access To Data Is Required? Inquiry/Read, Update/Deletion?',
          ),
        ],
      },
      {
        id: 'vulnerability-assessment',
        title: 'Vulnerability Assessment',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        questionGridSingleColumn: true,
        code15aItIsShowUpload: false,
        internalAssessmentStorageRowId: 'n15a_75',
        internalAssessmentBlock: {
          label:
            "Internal Assessment On Compliance Controls: (For PNBIL's Internal Use)",
          placeholder: 'Write here...',
        },
        questions: [
          it15('6.1', 'What Is The Frequency Of Penetration Testing Of Your IT Systems?'),
          it15(
            '6.2',
            "Vendor Willing To Share The Latest Audit Report On Cyber/ Information Security And Also The Penetration Testing's Reports, On Bank's Request?",
          ),
          it15('6.3', 'What Are Cyber Security Controls In Place?'),
        ],
      },
    ],
  },
  a15b: {
    annexureId: 'a15b',
    variant: 'questionnaire-sections',
    questionnaireUi: 'onboarding2Code15b',
    subtitle:
      'Self-certify security controls across domains, upload self-certification and evidence, then assign approvers and stakeholders.',
    fieldLabel: 'Data Request Input',
    numberBadge: 'square',
    showSupportingUpload: false,
    selfCertificationBanner: {
      title: 'Self Certification',
      downloadLabel: 'Download',
      downloadHref: ANNEXURE_15B_SELF_CERTIFICATION_TEMPLATE.href,
      downloadFileName: ANNEXURE_15B_SELF_CERTIFICATION_TEMPLATE.fileName,
    },
    selfCertificationUploadBlock: {
      title: 'Self Certification',
      description:
        'Upload signed self-certification documents and supporting compliance declarations for verification.',
    },
    sections: [
      {
        id: 'b15-governance',
        title: 'Governance',
        completionLabel: '—',
        showSectionalComments: false,
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15b_grp_1_8',
        storageRowIds: ['n15b_1', 'n15b_2', 'n15b_3', 'n15b_4', 'n15b_5', 'n15b_6', 'n15b_7', 'n15b_8'],
        questions: [
          {
            text: 'Whether the third party/vendor has (board/top management approved) information security policy in place with periodic reviews (minimum annually) by top management?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the information security policy is communicated to all employees and does the entity monitor compliance with the policy?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether documented standard operating procedures (SOPs) are in place for operational processes including, where applicable, business continuity management, backup management, patch management, change management, problem management, capacity management, access management, incident management, and logging and monitoring?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor has deployed a dedicated information security team independent of IT, with defined responsibilities, adequate skills, and appropriate reporting into senior management?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether suitable security certifications (e.g. ISO 27001, PCI-DSS, SOC 1, SOC 2, or equivalent) evidencing the security posture of the vendor environment are in place and current?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Where any work or part of work is outsourced by the third party to another party (subletting), whether prior consent of the bank is obtained, due diligence is performed on sub-service providers, and contractual flow-down of security obligations is maintained?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether required approvals are in place for sharing data with any third party, including subprocessors and partners?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the SLA/agreement with the bank includes clauses such as: right to audit (including to PNBIL), right to recall data, incident notification and cooperation, sub-outsourcing restrictions, data location and cross-border controls, liability and indemnity, exit and data return/destruction, and compliance with applicable laws and regulatory directions?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-hr-security',
        title: 'HR Security',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        storageRowIds: ['n15b_27'],
        questions: [
          {
            text: 'Whether background verification of the officials of the third party is completed before onboarding?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-access-management',
        title: 'Access Management',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15b_grp_28_32',
        storageRowIds: ['n15b_28', 'n15b_29', 'n15b_30', 'n15b_31', 'n15b_32'],
        questions: [
          {
            text: 'Whether privilege access to the third party/vendor environment is permitted from internet?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor configures or provides access to officials based on a documented and approved role conflict matrix?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether all default admin and root users are deleted/disabled and access is based on user specific IDs and all such accesses are logged?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party has deployed Active Directory (AD), Single Sign On (SSO) and strong password policy for end point and application access?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether proper access control is defined for protecting PNBIL data and access to the data is strictly on need-to-know basis?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-network-security',
        title: 'Network Security',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15b_sec_network_upload',
        storageRowIds: ['n15b_33', 'n15b_34', 'n15b_35', 'n15b_36', 'n15b_37', 'n15b_38', 'n15b_39', 'n15b_40'],
        questions: [
          {
            text: "Whether the third party/vendor's environment is suitably protected from external threats by way of firewall, IDS/IPS, AV, DLP, etc.?",
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: "Whether the third party/vendor's environment is suitably protected from external threats by way of WAF, NAC, etc.?",
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether rules are implemented on firewalls of the third party/vendor environment as per their approved process? Whether the entity has processes in place to review the firewall periodically?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor environment is segregated into militarized zone (MZ) and demilitarized zone (DMZ) separated by firewall, where any access from an external entity is permitted through DMZ only?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor follows the best practices of creation of separate network zones (VLAN segments) for production and non-production such as UAT?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor follows the best practices of creation of separate network zones (VLAN segments) for web, app, DB, critical and non-critical applications?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party has a separate network architecture diagram specific to integration with PNBIL? Whether internet access is restricted on internal servers, database servers, and any other servers?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the application and database is/will be hosted separately on dedicated infrastructure (physical/logical) for PNBIL?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-app-security',
        title: 'App Security',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15b_sec_app_upload',
        storageRowIds: ['n15b_41', 'n15b_42', 'n15b_43'],
        questions: [
          {
            text: 'Whether auditors are engaged by the third party for ensuring security posture of their applications? Security testing includes but is not limited to AppSec, API testing, source code review, VA, PT, SCD, DFRA, process review, access control, etc.',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor has deployed any open source or free software in their environment? If yes, whether processes are in place for closure of vulnerabilities and regular/timely patching for such software?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Where PNBIL data is permitted and required to be shared, whether only the bare minimum data is being shared? (Please document the need for sharing every data field.)',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-data-security',
        title: 'Data Security',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotIdEnd: 'n15b_sec_data_item9',
        storageRowIds: ['n15b_45', 'n15b_46', 'n15b_47', 'n15b_48', 'n15b_49', 'n15b_50', 'n15b_51', 'n15b_52'],
        questions: [
          {
            text: 'Whether the third party/vendor is permitted and required to store the data owned by PNBIL? If so, whether relevant approval to that effect is obtained? What are the security measures for safe storage and timely retrieval of data?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor is permitted to outsource the activity or share PNBIL-specific data to any other party, partly or fully, for any purpose? If so, are the specific activities/data elements and purpose documented and made part of the SLA?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor is permitted to take any crucial decisions on behalf of PNBIL without specific written approval from the IT application owner/BU? If not, how are such instances being reported?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether suitable security certificates such as ISO 27017 and ISO 27018 for cloud services and PCI DSS where PII data are stored/processed are in place?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the data shared by PNBIL is secured while in transit, processing, at rest, during backup and archival, on external media, etc., with latest and secure encryption standards?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether processes are in place to permanently erase PNBIL data after processing or after a clearly defined retention period by the third party/vendor? How will this be monitored?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Data must not be shared with outsiders without explicit and case-specific approval of PNBIL.',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Data should not be allowed to be downloaded or to prepare copies unless explicitly approved.',
            obligatory: 'Mandatory',
            required: true,
            badgeNum: 9,
          },
        ],
      },
      {
        id: 'b15-cloud-security',
        title: 'Cloud Security',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        storageRowIds: ['n15b_53'],
        questions: [
          {
            text: 'The key used by the vendor to encrypt PNBIL data should be different, i.e. it should not be the same that was/is used for other clients.',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-log-management',
        title: 'Log Management',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15b_sec_log_upload',
        storageRowIds: ['n15b_56', 'n15b_57', 'n15b_58'],
        questions: [
          {
            text: 'Whether proper log generation, storage, management and analysis happens for the third party/vendor application (including DFRA and access logs)?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the privilege access activities are logged, monitored, controlled and governed preferably using privilege identity management (PIM)?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the third party/vendor has captive SOC or managed service SOC for monitoring their systems and operations?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-incident-response',
        title: 'Incident Response',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        storageRowIds: ['n15b_59'],
        questions: [
          {
            text: 'Whether the third party vendor has witnessed any security or privacy breach in the past 2 years?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
      {
        id: 'b15-business-continuity',
        title: 'Business Continuity',
        completionLabel: '—',
        showSectionalComments: false,
        sectionNumberBadge: 'square',
        inlineSupportingDocument: true,
        inlineSupportingDocumentVariant: 'stackedCentered',
        supportingDocumentTitleMuted: true,
        supportingDocumentSizeHint: 'One file - max 10.0 MB',
        sectionUploadSlotId: 'n15b_sec_bc_upload',
        storageRowIds: ['n15b_60', 'n15b_61'],
        questions: [
          {
            text: 'Whether third party/vendor has deployed secure environments for their application for production?',
            obligatory: 'Mandatory',
            required: true,
          },
          {
            text: 'Whether the vendor performs periodic DR drills?',
            obligatory: 'Mandatory',
            required: true,
          },
        ],
      },
    ],
  },
};

export function getOb2AnnexureFormDefinition(annexureId: string | undefined): Ob2AnnexureFormDefinition | undefined {
  if (!annexureId) return undefined;
  return DEFINITIONS[annexureId];
}

/** Admin annexure-management API codes that differ from Onboarding2 definition ids. */
const ADMIN_API_CODE_TO_OB2_ID: Record<string, string> = {
  '11': 'a11',
};

/** Registered-vendor fill codes that differ from admin mapping (e.g. API 12 = vendor visit, not BCP). */
const VENDOR_FILL_API_CODE_TO_OB2_ID: Record<string, string> = {
  '12': 'a12',
};

function getOb2AnnexureIdForVendorFill(apiCode: string | undefined): string | undefined {
  const c = String(apiCode ?? '').trim();
  return VENDOR_FILL_API_CODE_TO_OB2_ID[c];
}

/** Map API annexure codes (`4`, `15A`, …) to static Onboarding2 designer definition ids (`a4`, `a15a`, …). */
export function getOb2AnnexureIdForApiCode(apiCode: string | undefined): string | undefined {
  if (!apiCode?.trim()) return undefined;
  const raw = apiCode.trim();
  const adminMapped = ADMIN_API_CODE_TO_OB2_ID[raw];
  if (adminMapped && DEFINITIONS[adminMapped]) return adminMapped;
  const compact = raw.toLowerCase().replace(/\s+/g, '');
  if (DEFINITIONS[compact]) return compact;
  const prefixed = compact.startsWith('a') ? compact : `a${compact}`;
  if (DEFINITIONS[prefixed]) return prefixed;
  if (/^15a$/i.test(raw)) return 'a15a';
  if (/^15b$/i.test(raw)) return 'a15b';
  return undefined;
}

const CODE_4_TAIL_SUPPORTING_SECTION: Ob2AnnexureQuestionnaireSection = {
  id: 'supporting-document-tail',
  title: 'Supporting Document',
  completionLabel: '—',
  weightingScoreDisplay: '0.00/100',
  showSectionalComments: false,
  questions: [],
  inlineSupportingDocument: true,
  inlineSupportingDocumentVariant: 'stackedCentered',
  supportingDocumentUploadTone: 'maroon',
  sectionUploadSlotId: 'n4_sec_support_b',
};

function ob2Code4SectionsThroughReporting(
  sections: NonNullable<Ob2AnnexureFormDefinition['sections']>,
  options?: {
    inlineSupportingDocumentOnReporting?: boolean;
    /** Second supporting-doc block after the Reporting section (admin preview). */
    appendTailSupportingSection?: boolean;
  },
): NonNullable<Ob2AnnexureFormDefinition['sections']> {
  const reportingIdx = sections.findIndex((s) => s.id === 'reporting');
  if (reportingIdx < 0) return sections;
  const trimmed = sections.slice(0, reportingIdx + 1);
  const withReportingUpload: Ob2AnnexureQuestionnaireSection[] = options?.inlineSupportingDocumentOnReporting
    ? trimmed.map((section, index): Ob2AnnexureQuestionnaireSection =>
        index === reportingIdx
          ? {
              ...section,
              inlineSupportingDocument: true,
              inlineSupportingDocumentVariant: 'stackedCentered',
              supportingDocumentUploadTone: 'maroon',
              supportingDocumentTitleMuted: true,
              supportingDocumentSizeHint: 'One file · max 10.0 MB',
              sectionUploadSlotId: 'n4_sec_support_a',
            }
          : section,
      )
    : trimmed;
  if (!options?.appendTailSupportingSection) return withReportingUpload;
  return [...withReportingUpload, CODE_4_TAIL_SUPPORTING_SECTION];
}

/**
 * Questionnaire definition for VMS admin annexure view — Code 4 through Reporting,
 * inline upload on Reporting, plus one tail Supporting Document section.
 */
export function ob2AnnexureDefinitionForAdminView(apiCode: string | undefined): Ob2AnnexureFormDefinition | undefined {
  const ob2Id = getOb2AnnexureIdForApiCode(apiCode);
  const def = getOb2AnnexureFormDefinition(ob2Id);
  if (!def?.sections?.length) return def;
  if (def.questionnaireUi !== 'onboarding2Code4') return def;
  return {
    ...def,
    sections: ob2Code4SectionsThroughReporting(def.sections, {
      inlineSupportingDocumentOnReporting: true,
      appendTailSupportingSection: true,
    }),
  };
}

/** Vendor fill: trim Code 4 sections (inline upload on Reporting; no admin-only tail sections). */
export function applyCode4VendorFillSections(
  sections: NonNullable<Ob2AnnexureFormDefinition['sections']>
): NonNullable<Ob2AnnexureFormDefinition['sections']> {
  return ob2Code4SectionsThroughReporting(sections, { inlineSupportingDocumentOnReporting: true });
}

/**
 * Onboarding / ongoing annexure fill — Code 4 ends at Reporting with one inline supporting upload
 * (not two extra “Supporting Document” accordion sections).
 */
export function ob2AnnexureDefinitionForVendorFill(apiCode: string | undefined): Ob2AnnexureFormDefinition | undefined {
  const ob2Id = getOb2AnnexureIdForVendorFill(apiCode) ?? getOb2AnnexureIdForApiCode(apiCode);
  const def = getOb2AnnexureFormDefinition(ob2Id);
  if (!def?.sections?.length) return def;
  if (def.questionnaireUi !== 'onboarding2Code4') return def;
  return {
    ...def,
    sections: ob2Code4SectionsThroughReporting(def.sections, { inlineSupportingDocumentOnReporting: true }),
  };
}
